import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
const TOKEN_KEY = "ezisell.access_token";

/** Buy Sources timeouts — do not rely on global client timeout (45000). */
export const BUY_SOURCES_SEARCH_TIMEOUT_MS = 300000;
export const BUY_SOURCES_PREVIEW_TIMEOUT_MS = 120000;

const client = axios.create({ baseURL: BASE, timeout: 45000 });

client.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    /* ignore */
  }
  if (config.headers) {
    delete config.headers["X-Workspace-Id"];
    delete config.headers["x-workspace-id"];
  }
  return config;
});

export function setAccessToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getAccessToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/** In-memory session cache for merchant icons (object URLs), keyed by domain.
 * Prevents N blob fetches/leaks for the same merchant across cards. */
const merchantIconCache = new Map();
const merchantIconPending = new Map();

/**
 * Fetch a merchant favicon through Ezisell (authenticated), returning an object
 * URL. The browser never contacts a third-party favicon service. Returns null
 * on failure so the card can fall back to initials. Deduplicates within the
 * session by normalized domain.
 */
export async function fetchMerchantIconUrl(domain) {
  const key = String(domain || "").trim().toLowerCase();
  if (!key) return null;
  if (merchantIconCache.has(key)) return merchantIconCache.get(key);
  if (merchantIconPending.has(key)) return merchantIconPending.get(key);

  const pending = client
    .get("/buy-sources/merchant-icon", {
      params: { domain: key },
      responseType: "blob",
      timeout: 15000,
    })
    .then((res) => {
      if (!res?.data || !(res.data instanceof Blob) || res.data.size === 0) return null;
      const url = URL.createObjectURL(res.data);
      merchantIconCache.set(key, url);
      return url;
    })
    .catch(() => {
      // Missing/unresolvable/unsafe icon: null -> initials fallback.
      return null;
    })
    .finally(() => {
      merchantIconPending.delete(key);
    });

  merchantIconPending.set(key, pending);
  return pending;
}

export async function get(path, config) {
  const { data } = await client.get(path, config);
  return data;
}

export async function post(path, body, config) {
  const { data } = await client.post(path, body, config);
  return data;
}

export async function patch(path, body) {
  const { data } = await client.patch(path, body);
  return data;
}

export async function del(path) {
  const { data } = await client.delete(path);
  return data;
}

/**
 * Unwrap Ezisell API envelope `{ data, data_mode, ... }` exactly once.
 * Also accepts a raw Axios response `{ data: envelope, status, ... }`.
 */
export function unwrapEnvelope(res) {
  if (res == null) return res;

  const isAxiosResponse =
    typeof res === "object" &&
    res.data != null &&
    (typeof res.status === "number" || res.headers != null || res.config != null);

  if (isAxiosResponse) {
    return unwrapEnvelope(res.data);
  }

  const isEzisellEnvelope =
    typeof res === "object" &&
    res.data != null &&
    ("data_mode" in res || "request_id" in res || "generated_at" in res);

  if (isEzisellEnvelope) {
    return res.data;
  }

  return res;
}

/** Prevent /api/v1 + /api/v1/... double-prefix when using axios client baseURL. */
export function normalizeBuySourceImagePath(imageEndpoint) {
  if (!imageEndpoint) return imageEndpoint;
  if (/^https?:\/\//i.test(imageEndpoint)) return imageEndpoint;

  let path = imageEndpoint.startsWith("/") ? imageEndpoint : `/${imageEndpoint}`;
  const base = String(BASE || "").replace(/\/$/, "");
  if (base.endsWith("/api/v1") && path.startsWith("/api/v1/")) {
    path = path.slice("/api/v1".length) || "/";
  }
  return path;
}

/**
 * Fetch signed buy-source preview screenshot.
 * GET image_endpoint with X-Buy-Source-Preview-Token header.
 * Returns a blob object URL — caller must revoke when done.
 */
export async function fetchBuySourcePreviewImage(imageEndpoint, previewToken) {
  if (!imageEndpoint || !previewToken) {
    throw new Error("preview_image_request_invalid");
  }
  const headers = { "X-Buy-Source-Preview-Token": previewToken };
  const config = {
    headers,
    responseType: "blob",
    timeout: BUY_SOURCES_PREVIEW_TIMEOUT_MS,
  };
  const isAbsolute = /^https?:\/\//i.test(imageEndpoint);
  const response = isAbsolute
    ? await axios.get(imageEndpoint, config)
    : await client.get(normalizeBuySourceImagePath(imageEndpoint), config);
  return URL.createObjectURL(response.data);
}

/** Build analyze query string from live params (itemId/url/upc/q). */
function analyzeQuery(params = {}) {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", String(params.q).trim());
  if (params.itemId) qs.set("itemId", String(params.itemId).trim());
  if (params.url) qs.set("url", String(params.url).trim());
  if (params.upc) qs.set("upc", String(params.upc).trim());
  if (params.force) qs.set("force", "true");
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export const api = {
  meta: () => get("/meta"),
  authSession: (email, org_name) =>
    post("/auth/session", { email, org_name: org_name || undefined }),
  authMe: () => get("/auth/me"),
  product: (params = {}) => get(`/analyze/product${analyzeQuery(params)}`),
  history: (params = {}) => get(`/analyze/history${analyzeQuery(params)}`),
  attributes: (params = {}) => get(`/analyze/attributes${analyzeQuery(params)}`),
  bundle: (params = {}) => get(`/analyze/bundle${analyzeQuery(params)}`),
  calculate: (body) => post("/analyze/calculate", body),
  leads: () => get("/leads"),
  finderTemplates: () => get("/finder/templates"),
  finderResults: () => get("/finder/results"),
  topSellers: () => get("/finder/top-sellers"),
  catalogJob: () => get("/catalog/job"),
  receipts: () => get("/receipts"),
  receiptDetail: () => get("/receipts/detail"),
  inventory: () => get("/inventory"),
  sales: () => get("/sales"),
  ppc: () => get("/ppc"),
  wfs: () => get("/wfs"),
  watchlists: () => get("/watchlists"),
  alerts: () => get("/alerts"),
  scanHistory: () => get("/scan-history"),
  integrations: () => get("/integrations"),
  integrationsList: () => get("/integrations"),
  plans: () => get("/billing/plans"),
  billingSummary: () => get("/billing"),
  sandboxCheckout: (plan_id = "growth") =>
    post("/billing/sandbox-checkout", { plan_id }),
  usage: () => get("/usage"),
  team: () => get("/team"),

  accountProfile: () => get("/account/profile"),
  patchAccountProfile: (body) => patch("/account/profile", body),
  accountPreferences: () => get("/account/preferences"),
  patchAccountPreferences: (body) => patch("/account/preferences", body),
  accountDevices: () => get("/account/devices"),
  revokeDevice: (sessionId) =>
    post(`/account/devices/${encodeURIComponent(sessionId)}/revoke`, {}),
  revokeOtherDevices: () => post("/account/devices/revoke-others", {}),

  walmartVerify: (body) => post("/integrations/walmart/verify", body),
  walmartConnect: (body) => post("/integrations/walmart/connect", body),
  walmartDisconnect: () => post("/integrations/walmart/disconnect", {}),
  walmartReplaceCredentials: (body) =>
    post("/integrations/walmart/credentials", body),
  walmartUpdateSettings: (body) => patch("/integrations/walmart/settings", body),
  walmartIntegration: () => get("/integrations/walmart"),
  dashboard: () => get("/dashboard"),

  productSourcingHealth: () => get("/product-sourcing/health"),
  productSourcingSources: (enabledOnly = true) =>
    get(`/product-sourcing/sources?enabled_only=${enabledOnly ? "true" : "false"}`),
  productSourcingCreateJob: (body) => post("/product-sourcing/jobs", body),
  productSourcingJob: (jobId) => get(`/product-sourcing/jobs/${encodeURIComponent(jobId)}`),
  productSourcingOpportunities: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    const s = qs.toString();
    return get(`/product-sourcing/opportunities${s ? `?${s}` : ""}`);
  },
  productSourcingOpportunity: (id) =>
    get(`/product-sourcing/opportunities/${encodeURIComponent(id)}`),
  productSourcingReview: (id, review_status) =>
    post(`/product-sourcing/opportunities/${encodeURIComponent(id)}/review`, {
      review_status,
    }),
  productSourcingRefresh: (id) =>
    post(`/product-sourcing/opportunities/${encodeURIComponent(id)}/refresh`, {}),
  productSourcingHistory: (id) =>
    get(`/product-sourcing/opportunities/${encodeURIComponent(id)}/history`),
  // History for a SAVED LEAD, resolved from its own persisted identity so it
  // works after the sourcing run that produced it is gone. Exact retailer/Walmart
  // pairing via opportunity_key; never a title-similarity guess.
  savedLeadHistory: ({ walmartItemId, sourceId, sourceProductId } = {}) => {
    const qs = new URLSearchParams();
    if (walmartItemId) qs.set("walmart_item_id", walmartItemId);
    if (sourceId) qs.set("source_id", sourceId);
    if (sourceProductId) qs.set("source_product_id", sourceProductId);
    return get(`/product-sourcing/saved-leads/history?${qs.toString()}`);
  },
  productSourcingCollections: () => get("/product-sourcing/collections"),
  productSourcingCreateCollection: (name) =>
    post("/product-sourcing/collections", { name }),
  productSourcingAddToCollection: (collectionId, opportunityId) =>
    post(`/product-sourcing/collections/${encodeURIComponent(collectionId)}/items`, {
      opportunity_id: opportunityId,
    }),
  productSourcingRemoveFromCollection: (collectionId, opportunityId) =>
    del(`/product-sourcing/collections/${encodeURIComponent(collectionId)}/items/${encodeURIComponent(opportunityId)}`),
  productSourcingFlag: (id, reason) =>
    post(`/product-sourcing/opportunities/${encodeURIComponent(id)}/flag`, { reason }),
  productSourcingUnflag: (id) =>
    del(`/product-sourcing/opportunities/${encodeURIComponent(id)}/flag`),
  productSourcingTracking: (id) =>
    get(`/product-sourcing/opportunities/${encodeURIComponent(id)}/tracking`),
  productSourcingSetTracking: (id, enabled) =>
    post(`/product-sourcing/opportunities/${encodeURIComponent(id)}/tracking`, { enabled }),

  quickPicks: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    const s = qs.toString();
    return get(`/quick-picks${s ? `?${s}` : ""}`);
  },
  quickPicksSummary: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    const s = qs.toString();
    return get(`/quick-picks/summary${s ? `?${s}` : ""}`);
  },
  quickPick: (id) => get(`/quick-picks/${encodeURIComponent(id)}`),

  productSearchesStatus: () => get("/product-searches/status"),
  productSearchesCreate: (body) => post("/product-searches", body),
  productSearchesRecent: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    const s = qs.toString();
    return get(`/product-searches/recent${s ? `?${s}` : ""}`);
  },
  productSearchGet: (jobId) =>
    get(`/product-searches/${encodeURIComponent(jobId)}`),
  productSearchProgress: (jobId) =>
    get(`/product-searches/${encodeURIComponent(jobId)}/progress`),
  productSearchResults: (jobId) =>
    get(`/product-searches/${encodeURIComponent(jobId)}/results`),
  productSearchCancel: (jobId) =>
    post(`/product-searches/${encodeURIComponent(jobId)}/cancel`, {}),
  productSearchSave: (jobId, body) =>
    post(`/product-searches/${encodeURIComponent(jobId)}/save`, body),
  productSearchDismiss: (jobId, body) =>
    post(`/product-searches/${encodeURIComponent(jobId)}/dismiss`, body),

  /** Buy Sources — explicit long timeouts; no calls on page render. */
  buySourcesSearch: (body) =>
    post("/buy-sources/search", body, { timeout: BUY_SOURCES_SEARCH_TIMEOUT_MS }),
  /** Storefront Hunter auto-source batches — same timeout budget as single search. */
  buySourcesSearchBatch: (body) =>
    post("/buy-sources/search-batch", body, { timeout: BUY_SOURCES_SEARCH_TIMEOUT_MS }),
  buySourcesPreviewRequest: (previewToken) =>
    post(
      "/buy-sources/preview",
      { preview_token: previewToken },
      { timeout: BUY_SOURCES_PREVIEW_TIMEOUT_MS }
    ),
  buySourcesPreviewImage: fetchBuySourcePreviewImage,

  searchLocationGet: () => get("/search-location"),
  searchLocationPatch: (postal_code) => patch("/search-location", { postal_code }),

  /** Storefront Hunter — Walmart seller catalog only; no Buy Sources on analyze. */
  storefrontHunterAnalyze: (seller, opts = {}) => {
    const page = opts.page ?? 1;
    const page_size = opts.pageSize ?? 40;
    const qs = new URLSearchParams({ page: String(page), page_size: String(page_size) });
    return post(`/storefront-hunter/analyze?${qs.toString()}`, { seller });
  },
  storefrontHunterProducts: (sellerId, opts = {}) => {
    const page = opts.page ?? 1;
    const page_size = opts.pageSize ?? 40;
    const qs = new URLSearchParams({ page: String(page), page_size: String(page_size) });
    return get(
      `/storefront-hunter/sellers/${encodeURIComponent(sellerId)}/products?${qs.toString()}`
    );
  },
  storefrontHunterCapabilities: () => get("/storefront-hunter/capabilities"),
  storefrontHunterEnrich: (body) =>
    post("/storefront-hunter/enrich", body, { timeout: 45000 }),
};
